import React, { useMemo, useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import {
  IconPlus,
  IconPencil,
  IconTrash,
  IconEye,
  IconArrowUp,
  IconArrowDown,
  IconExternalLink,
} from '@tabler/icons-react';
import { useApp } from '../context/AppContext';
import { useToast } from '../context/ToastContext';
import { useLoading } from '../context/LoadingContext';
import ConfirmModal from '../components/ConfirmModal';
import SelectField from '../components/SelectField';

const WRITES_TO_SHEET = Boolean(process.env.REACT_APP_APPS_SCRIPT_URL);

const ROLE_STATUSES = ['Open', 'Filled', 'Closed'];
const ROLE_STATUS_STYLES = {
  Open: 'bg-success-bg text-success-tx',
  Filled: 'bg-light-gold text-bronze',
  Closed: 'bg-neutral-bg text-neutral-tx',
};

const EMPTY_ROLE_FORM = {
  title: '',
  status: 'Open',
  jobPostingTitle: '',
  jobPostingLink: '',
  jobPostingDescription: '',
};

const EMPTY_APPLICANT_FORM = {
  roleId: '',
  name: '',
  availability: '',
  cvLink: '',
  introVideoLink: '',
  testLinks: '',
};

// Same inline re-auth pattern as Clients/Holidays/Announcements — a quick
// Google sign-in right in the modal footer when the token's gone stale.
function ReauthFooter({ setIdToken, onCancel }) {
  const { showError } = useToast();
  return (
    <div className="flex flex-col items-center gap-3">
      <p className="text-sm text-muted text-center">Your session needs a quick refresh before this can be saved.</p>
      <GoogleLogin
        onSuccess={(credentialResponse) => setIdToken(credentialResponse.credential)}
        onError={() => showError('Google sign-in failed — please try again.')}
      />
      <button onClick={onCancel} className="text-sm font-medium text-muted hover:text-ink transition-colors">
        Cancel
      </button>
    </div>
  );
}

// Candidate pipeline: pick a client, then manage that client's open Roles
// and the Applicants put forward for them. Deliberately separate from the
// Clients tab's "Manage staff" — Staff is who's actually working the
// client; this is who's being considered to join them.
export default function Requirements() {
  const {
    clients,
    roles,
    roleCriteria,
    applicants,
    applicantAnswers,
    createRole,
    updateRole,
    deleteRole,
    updateRoleCriteria,
    createApplicant,
    updateApplicant,
    deleteApplicant,
    setIdToken,
    isIdTokenValid,
  } = useApp();
  const { showSuccess, showError } = useToast();
  const { withLoading } = useLoading();
  const needsReauth = WRITES_TO_SHEET && !isIdTokenValid;

  const sortedClients = useMemo(() => [...clients].sort((a, b) => a.name.localeCompare(b.name)), [clients]);

  const [selectedClientId, setSelectedClientId] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [applicantRoleFilter, setApplicantRoleFilter] = useState('All');

  const clientRoles = useMemo(
    () => roles.filter((r) => r.client_id === selectedClientId).sort((a, b) => a.title.localeCompare(b.title)),
    [roles, selectedClientId]
  );
  const filteredRoles = useMemo(
    () => (statusFilter === 'All' ? clientRoles : clientRoles.filter((r) => r.status === statusFilter)),
    [clientRoles, statusFilter]
  );

  const clientRoleIds = useMemo(() => new Set(clientRoles.map((r) => r.role_id)), [clientRoles]);
  const roleTitleById = useMemo(() => {
    const map = {};
    clientRoles.forEach((r) => {
      map[r.role_id] = r.title;
    });
    return map;
  }, [clientRoles]);

  const clientApplicants = useMemo(
    () => applicants.filter((a) => clientRoleIds.has(a.role_id)).sort((a, b) => a.name.localeCompare(b.name)),
    [applicants, clientRoleIds]
  );
  const filteredApplicants = useMemo(
    () =>
      applicantRoleFilter === 'All'
        ? clientApplicants
        : clientApplicants.filter((a) => a.role_id === applicantRoleFilter),
    [clientApplicants, applicantRoleFilter]
  );

  const selectClient = (clientId) => {
    setSelectedClientId(clientId);
    setStatusFilter('All');
    setApplicantRoleFilter('All');
  };

  // --- Role create/edit/delete — criteria (the Q&A list a role's
  // applicants answer) is edited right in this same form, not as a
  // separate step, since a role's questions are part of defining the role. ---
  const [roleFormOpen, setRoleFormOpen] = useState(false);
  const [editingRoleId, setEditingRoleId] = useState(null);
  const [roleForm, setRoleForm] = useState(EMPTY_ROLE_FORM);
  const [showJobPosting, setShowJobPosting] = useState(false);
  const [criteriaDraft, setCriteriaDraft] = useState([]); // [{ criteriaId?, label }]
  const [deleteRoleTarget, setDeleteRoleTarget] = useState(null);

  const openCreateRole = () => {
    setEditingRoleId(null);
    setRoleForm(EMPTY_ROLE_FORM);
    setShowJobPosting(false);
    setCriteriaDraft([]);
    setRoleFormOpen(true);
  };

  const openEditRole = (r) => {
    setEditingRoleId(r.role_id);
    setRoleForm({
      title: r.title,
      status: r.status || 'Open',
      jobPostingTitle: r.job_posting_title || '',
      jobPostingLink: r.job_posting_link || '',
      jobPostingDescription: r.job_posting_description || '',
    });
    setShowJobPosting(Boolean(r.job_posting_title || r.job_posting_link || r.job_posting_description));
    setCriteriaDraft(
      roleCriteria
        .filter((c) => c.role_id === r.role_id)
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((c) => ({ criteriaId: c.criteria_id, label: c.label }))
    );
    setRoleFormOpen(true);
  };

  const closeRoleForm = () => setRoleFormOpen(false);

  const addCriteriaRow = () => setCriteriaDraft((prev) => [...prev, { label: '' }]);
  const removeCriteriaRow = (index) => setCriteriaDraft((prev) => prev.filter((_, i) => i !== index));
  const moveCriteriaRow = (index, direction) => {
    setCriteriaDraft((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };
  const updateCriteriaLabel = (index, label) =>
    setCriteriaDraft((prev) => prev.map((c, i) => (i === index ? { ...c, label } : c)));

  const confirmSaveRole = async () => {
    if (!roleForm.title.trim()) {
      showError('Give the role a title.');
      return;
    }
    if (criteriaDraft.some((c) => !c.label.trim())) {
      showError('Every question needs a label — remove any blank ones.');
      return;
    }
    const fields = {
      title: roleForm.title.trim(),
      status: roleForm.status,
      jobPostingTitle: roleForm.jobPostingTitle.trim(),
      jobPostingLink: roleForm.jobPostingLink.trim(),
      jobPostingDescription: roleForm.jobPostingDescription.trim(),
    };
    const criteria = criteriaDraft.map((c, index) => ({
      criteriaId: c.criteriaId,
      label: c.label.trim(),
      sortOrder: index,
    }));
    try {
      if (editingRoleId) {
        await withLoading(async () => {
          await updateRole({ roleId: editingRoleId, fields });
          await updateRoleCriteria({ roleId: editingRoleId, criteria });
        });
        showSuccess('Role updated.');
      } else {
        await withLoading(async () => {
          const newRoleId = await createRole({ clientId: selectedClientId, fields });
          if (criteria.length > 0) {
            await updateRoleCriteria({ roleId: newRoleId, criteria });
          }
        });
        showSuccess('Role added.');
      }
      closeRoleForm();
    } catch (err) {
      showError(err.message || "Couldn't save that — please try again.");
    }
  };

  const confirmDeleteRole = async () => {
    if (!deleteRoleTarget) return;
    try {
      await withLoading(() => deleteRole({ roleId: deleteRoleTarget }));
      showSuccess('Role deleted.');
      setDeleteRoleTarget(null);
    } catch (err) {
      showError(err.message || "Couldn't delete that — please try again.");
    }
  };

  // --- Applicant create/edit/delete/view ---
  const [applicantFormOpen, setApplicantFormOpen] = useState(false);
  const [editingApplicantId, setEditingApplicantId] = useState(null);
  const [applicantForm, setApplicantForm] = useState(EMPTY_APPLICANT_FORM);
  const [answersDraft, setAnswersDraft] = useState({}); // { criteriaId: text }
  const [deleteApplicantTarget, setDeleteApplicantTarget] = useState(null);
  const [viewApplicantId, setViewApplicantId] = useState(null);

  const openCreateApplicant = () => {
    setEditingApplicantId(null);
    setApplicantForm({ ...EMPTY_APPLICANT_FORM, roleId: filteredRoles[0]?.role_id || clientRoles[0]?.role_id || '' });
    setAnswersDraft({});
    setApplicantFormOpen(true);
  };

  const openEditApplicant = (a) => {
    setEditingApplicantId(a.applicant_id);
    setApplicantForm({
      roleId: a.role_id,
      name: a.name,
      availability: a.availability || '',
      cvLink: a.cv_link || '',
      introVideoLink: a.intro_video_link || '',
      testLinks: a.test_links || '',
    });
    const existingAnswers = {};
    applicantAnswers
      .filter((ans) => ans.applicant_id === a.applicant_id)
      .forEach((ans) => {
        existingAnswers[ans.criteria_id] = ans.answer;
      });
    setAnswersDraft(existingAnswers);
    setApplicantFormOpen(true);
  };

  const closeApplicantForm = () => setApplicantFormOpen(false);

  const activeCriteriaForForm = useMemo(
    () =>
      roleCriteria
        .filter((c) => c.role_id === applicantForm.roleId)
        .sort((a, b) => a.sort_order - b.sort_order),
    [roleCriteria, applicantForm.roleId]
  );

  const confirmSaveApplicant = async () => {
    if (!applicantForm.name.trim()) {
      showError('Give the applicant a name.');
      return;
    }
    if (!editingApplicantId && !applicantForm.roleId) {
      showError('Pick which role this applicant is for.');
      return;
    }
    const fields = {
      name: applicantForm.name.trim(),
      availability: applicantForm.availability.trim(),
      cvLink: applicantForm.cvLink.trim(),
      introVideoLink: applicantForm.introVideoLink.trim(),
      testLinks: applicantForm.testLinks.trim(),
    };
    try {
      if (editingApplicantId) {
        await withLoading(() => updateApplicant({ applicantId: editingApplicantId, fields, answers: answersDraft }));
        showSuccess('Applicant updated.');
      } else {
        await withLoading(() =>
          createApplicant({ roleId: applicantForm.roleId, fields, answers: answersDraft })
        );
        showSuccess('Applicant added.');
      }
      closeApplicantForm();
    } catch (err) {
      showError(err.message || "Couldn't save that — please try again.");
    }
  };

  const confirmDeleteApplicant = async () => {
    if (!deleteApplicantTarget) return;
    try {
      await withLoading(() => deleteApplicant({ applicantId: deleteApplicantTarget }));
      showSuccess('Applicant deleted.');
      setDeleteApplicantTarget(null);
    } catch (err) {
      showError(err.message || "Couldn't delete that — please try again.");
    }
  };

  const viewApplicant = applicants.find((a) => a.applicant_id === viewApplicantId);
  const viewApplicantCriteria = useMemo(
    () =>
      viewApplicant
        ? roleCriteria.filter((c) => c.role_id === viewApplicant.role_id).sort((a, b) => a.sort_order - b.sort_order)
        : [],
    [viewApplicant, roleCriteria]
  );
  const viewApplicantAnswers = useMemo(() => {
    if (!viewApplicant) return {};
    const map = {};
    applicantAnswers
      .filter((ans) => ans.applicant_id === viewApplicant.applicant_id)
      .forEach((ans) => {
        map[ans.criteria_id] = ans.answer;
      });
    return map;
  }, [viewApplicant, applicantAnswers]);

  const selectedClient = clients.find((c) => c.client_id === selectedClientId);

  return (
    <>
      <div className="max-w-3xl mx-auto">
        <p className="text-sm text-muted">Manage open roles and the applicants put forward for each client.</p>

        <div className="mt-4">
          <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">Client</label>
          <div className="w-full sm:w-80">
            <SelectField value={selectedClientId} onChange={(e) => selectClient(e.target.value)} className="!py-2">
              <option value="">Select a client…</option>
              {sortedClients.map((c) => (
                <option key={c.client_id} value={c.client_id}>
                  {c.name}
                </option>
              ))}
            </SelectField>
          </div>
        </div>

        {!selectedClientId && <p className="text-sm text-muted text-center py-10">Pick a client to get started.</p>}

        {selectedClientId && (
          <div className="mt-6 space-y-8">
            {/* Roles */}
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div className="flex items-center gap-2">
                  <h2 className="text-sm font-semibold text-ink">Roles — {selectedClient?.name}</h2>
                  <div className="w-32">
                    <SelectField value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="!py-1 !text-xs">
                      <option value="All">All statuses</option>
                      {ROLE_STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </SelectField>
                  </div>
                </div>
                <button
                  onClick={openCreateRole}
                  className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl font-semibold text-white bg-gold hover:bg-bronze transition-colors text-sm whitespace-nowrap flex-shrink-0 self-start sm:self-auto"
                >
                  <IconPlus size={16} stroke={2} />
                  Add Role
                </button>
              </div>

              <div className="mt-3 space-y-2">
                {filteredRoles.length === 0 && (
                  <p className="text-sm text-muted text-center py-6">
                    {clientRoles.length === 0 ? 'No roles yet for this client.' : 'No roles match that filter.'}
                  </p>
                )}
                {filteredRoles.map((r) => (
                  <div key={r.role_id} className="bg-card border border-border rounded-xl p-3 flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-medium text-ink truncate">{r.title}</p>
                        <span
                          className={`inline-flex items-center rounded-full font-medium px-2 py-0.5 text-[10px] tracking-wide ${ROLE_STATUS_STYLES[r.status] || ROLE_STATUS_STYLES.Open}`}
                        >
                          {r.status}
                        </span>
                      </div>
                      {r.job_posting_link && (
                        <a
                          href={r.job_posting_link}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-bronze hover:text-ink transition-colors mt-1"
                        >
                          Job posting <IconExternalLink size={12} stroke={1.8} />
                        </a>
                      )}
                    </div>
                    <div className="flex-shrink-0 flex items-center gap-1">
                      <button
                        onClick={() => openEditRole(r)}
                        aria-label="Edit"
                        className="p-1.5 rounded-lg text-muted hover:text-ink hover:bg-card-tint transition-colors"
                      >
                        <IconPencil size={16} stroke={1.8} />
                      </button>
                      <button
                        onClick={() => setDeleteRoleTarget(r.role_id)}
                        aria-label="Delete"
                        className="p-1.5 rounded-lg text-muted hover:text-error-tx hover:bg-card-tint transition-colors"
                      >
                        <IconTrash size={16} stroke={1.8} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Applicants */}
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div className="flex items-center gap-2">
                  <h2 className="text-sm font-semibold text-ink">Applicants</h2>
                  <div className="w-36">
                    <SelectField
                      value={applicantRoleFilter}
                      onChange={(e) => setApplicantRoleFilter(e.target.value)}
                      className="!py-1 !text-xs"
                    >
                      <option value="All">All roles</option>
                      {clientRoles.map((r) => (
                        <option key={r.role_id} value={r.role_id}>
                          {r.title}
                        </option>
                      ))}
                    </SelectField>
                  </div>
                </div>
                <button
                  onClick={openCreateApplicant}
                  disabled={clientRoles.length === 0}
                  title={clientRoles.length === 0 ? 'Add a role first' : undefined}
                  className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl font-semibold text-white bg-gold hover:bg-bronze transition-colors text-sm whitespace-nowrap flex-shrink-0 self-start sm:self-auto disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-gold"
                >
                  <IconPlus size={16} stroke={2} />
                  Add Applicant
                </button>
              </div>

              <div className="mt-3 space-y-2">
                {clientRoles.length === 0 && (
                  <p className="text-sm text-muted text-center py-6">Add a role before logging applicants.</p>
                )}
                {clientRoles.length > 0 && filteredApplicants.length === 0 && (
                  <p className="text-sm text-muted text-center py-6">No applicants logged yet.</p>
                )}
                {filteredApplicants.map((a) => (
                  <div key={a.applicant_id} className="bg-card border border-border rounded-xl p-3 flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-ink truncate">{a.name}</p>
                      <p className="text-xs text-muted truncate">
                        {roleTitleById[a.role_id] || 'Unknown role'}
                        {a.availability && <> · {a.availability}</>}
                      </p>
                    </div>
                    <div className="flex-shrink-0 flex items-center gap-1">
                      <button
                        onClick={() => setViewApplicantId(a.applicant_id)}
                        aria-label="View"
                        title="View"
                        className="p-1.5 rounded-lg text-muted hover:text-ink hover:bg-card-tint transition-colors"
                      >
                        <IconEye size={16} stroke={1.8} />
                      </button>
                      <button
                        onClick={() => openEditApplicant(a)}
                        aria-label="Edit"
                        className="p-1.5 rounded-lg text-muted hover:text-ink hover:bg-card-tint transition-colors"
                      >
                        <IconPencil size={16} stroke={1.8} />
                      </button>
                      <button
                        onClick={() => setDeleteApplicantTarget(a.applicant_id)}
                        aria-label="Delete"
                        className="p-1.5 rounded-lg text-muted hover:text-error-tx hover:bg-card-tint transition-colors"
                      >
                        <IconTrash size={16} stroke={1.8} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Role create/edit */}
      <ConfirmModal
        open={roleFormOpen}
        title={editingRoleId ? 'Edit role' : 'New role'}
        confirmLabel="Save"
        onConfirm={confirmSaveRole}
        onCancel={closeRoleForm}
        footer={needsReauth ? <ReauthFooter setIdToken={setIdToken} onCancel={closeRoleForm} /> : undefined}
        size="lg"
      >
        <div className="max-h-[60vh] overflow-y-auto pr-3 scroll-thin space-y-3">
          <div>
            <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">Role title</label>
            <input
              type="text"
              value={roleForm.title}
              onChange={(e) => setRoleForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="e.g. SEO & Discoverability Specialist"
              className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">Status</label>
            <SelectField value={roleForm.status} onChange={(e) => setRoleForm((f) => ({ ...f, status: e.target.value }))} className="!py-2">
              {ROLE_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </SelectField>
          </div>

          <button
            type="button"
            onClick={() => setShowJobPosting((v) => !v)}
            className="text-xs font-semibold text-bronze hover:text-ink transition-colors"
          >
            {showJobPosting ? 'Hide' : 'Add'} job posting details (optional)
          </button>

          {showJobPosting && (
            <div className="space-y-3 border-t border-border pt-3">
              <div>
                <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">
                  Job posting title
                </label>
                <input
                  type="text"
                  value={roleForm.jobPostingTitle}
                  onChange={(e) => setRoleForm((f) => ({ ...f, jobPostingTitle: e.target.value }))}
                  placeholder="Title as posted (e.g. on OLJ)"
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">
                  Job posting link
                </label>
                <input
                  type="text"
                  value={roleForm.jobPostingLink}
                  onChange={(e) => setRoleForm((f) => ({ ...f, jobPostingLink: e.target.value }))}
                  placeholder="https://..."
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">
                  Job posting description
                </label>
                <textarea
                  value={roleForm.jobPostingDescription}
                  onChange={(e) => setRoleForm((f) => ({ ...f, jobPostingDescription: e.target.value }))}
                  rows={3}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold resize-none"
                />
              </div>
            </div>
          )}

          <div className="border-t border-border pt-3 space-y-2">
            <p className="text-xs font-medium text-muted uppercase tracking-wide">Criteria</p>
            {criteriaDraft.length === 0 && (
              <p className="text-sm text-muted">No questions yet — applicants for this role will just show the fixed fields.</p>
            )}
            {criteriaDraft.map((c, index) => (
              <div key={c.criteriaId || `new-${index}`} className="flex items-start gap-2">
                <input
                  type="text"
                  value={c.label}
                  onChange={(e) => updateCriteriaLabel(index, e.target.value)}
                  placeholder="e.g. Q1: Technical SEO issue you fixed"
                  className="flex-1 border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
                />
                <div className="flex-shrink-0 flex items-center gap-0.5">
                  <button
                    type="button"
                    onClick={() => moveCriteriaRow(index, -1)}
                    disabled={index === 0}
                    aria-label="Move up"
                    className="p-1.5 rounded-lg text-muted hover:text-ink hover:bg-card-tint transition-colors disabled:opacity-30"
                  >
                    <IconArrowUp size={16} stroke={1.8} />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveCriteriaRow(index, 1)}
                    disabled={index === criteriaDraft.length - 1}
                    aria-label="Move down"
                    className="p-1.5 rounded-lg text-muted hover:text-ink hover:bg-card-tint transition-colors disabled:opacity-30"
                  >
                    <IconArrowDown size={16} stroke={1.8} />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeCriteriaRow(index)}
                    aria-label="Remove"
                    className="p-1.5 rounded-lg text-muted hover:text-error-tx hover:bg-card-tint transition-colors"
                  >
                    <IconTrash size={16} stroke={1.8} />
                  </button>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={addCriteriaRow}
              className="flex items-center gap-1.5 text-xs font-semibold text-bronze hover:text-ink transition-colors"
            >
              <IconPlus size={14} stroke={2} />
              Add question
            </button>
          </div>
        </div>
      </ConfirmModal>

      {/* Role delete */}
      <ConfirmModal
        open={Boolean(deleteRoleTarget)}
        title="Delete this role?"
        confirmLabel="Delete"
        onConfirm={confirmDeleteRole}
        onCancel={() => setDeleteRoleTarget(null)}
        footer={needsReauth ? <ReauthFooter setIdToken={setIdToken} onCancel={() => setDeleteRoleTarget(null)} /> : undefined}
      >
        <p>This also removes its criteria and every applicant logged for it. This can't be undone.</p>
      </ConfirmModal>

      {/* Applicant create/edit */}
      <ConfirmModal
        open={applicantFormOpen}
        title={editingApplicantId ? 'Edit applicant' : 'New applicant'}
        confirmLabel="Save"
        onConfirm={confirmSaveApplicant}
        onCancel={closeApplicantForm}
        footer={needsReauth ? <ReauthFooter setIdToken={setIdToken} onCancel={closeApplicantForm} /> : undefined}
        size="lg"
      >
        <div className="max-h-[60vh] overflow-y-auto pr-3 scroll-thin space-y-3">
          <div>
            <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">Role</label>
            <SelectField
              value={applicantForm.roleId}
              onChange={(e) => setApplicantForm((f) => ({ ...f, roleId: e.target.value }))}
              disabled={Boolean(editingApplicantId)}
              className="!py-2 disabled:opacity-60"
            >
              {clientRoles.map((r) => (
                <option key={r.role_id} value={r.role_id}>
                  {r.title}
                </option>
              ))}
            </SelectField>
            {editingApplicantId && (
              <p className="text-[11px] text-muted mt-1">
                To put them forward for a different role, log them again as a new applicant instead.
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">Name</label>
            <input
              type="text"
              value={applicantForm.name}
              onChange={(e) => setApplicantForm((f) => ({ ...f, name: e.target.value }))}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">Availability</label>
            <input
              type="text"
              value={applicantForm.availability}
              onChange={(e) => setApplicantForm((f) => ({ ...f, availability: e.target.value }))}
              placeholder="e.g. Available now"
              className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">CV link</label>
            <input
              type="text"
              value={applicantForm.cvLink}
              onChange={(e) => setApplicantForm((f) => ({ ...f, cvLink: e.target.value }))}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">
              Intro video link
            </label>
            <input
              type="text"
              value={applicantForm.introVideoLink}
              onChange={(e) => setApplicantForm((f) => ({ ...f, introVideoLink: e.target.value }))}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">
              Test link(s)
            </label>
            <input
              type="text"
              value={applicantForm.testLinks}
              onChange={(e) => setApplicantForm((f) => ({ ...f, testLinks: e.target.value }))}
              placeholder="Separate multiple links with a space"
              className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
            />
          </div>

          {activeCriteriaForForm.length > 0 && (
            <div className="border-t border-border pt-3 space-y-3">
              <p className="text-xs font-medium text-muted uppercase tracking-wide">Criteria</p>
              {activeCriteriaForForm.map((c) => (
                <div key={c.criteria_id}>
                  <label className="block text-xs text-ink mb-1">{c.label}</label>
                  <textarea
                    value={answersDraft[c.criteria_id] || ''}
                    onChange={(e) => setAnswersDraft((d) => ({ ...d, [c.criteria_id]: e.target.value }))}
                    rows={2}
                    className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold resize-none"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </ConfirmModal>

      {/* Applicant delete */}
      <ConfirmModal
        open={Boolean(deleteApplicantTarget)}
        title="Delete this applicant?"
        confirmLabel="Delete"
        onConfirm={confirmDeleteApplicant}
        onCancel={() => setDeleteApplicantTarget(null)}
        footer={
          needsReauth ? <ReauthFooter setIdToken={setIdToken} onCancel={() => setDeleteApplicantTarget(null)} /> : undefined
        }
      >
        <p>This can't be undone.</p>
      </ConfirmModal>

      {/* Applicant view */}
      <ConfirmModal
        open={Boolean(viewApplicantId)}
        title={viewApplicant ? viewApplicant.name : 'Applicant'}
        footer={
          <button
            onClick={() => setViewApplicantId(null)}
            className="w-full py-2.5 rounded-xl font-semibold text-white bg-gold hover:bg-bronze transition-colors"
          >
            Close
          </button>
        }
        onConfirm={() => setViewApplicantId(null)}
        onCancel={() => setViewApplicantId(null)}
      >
        {viewApplicant && (
          <div className="max-h-[60vh] overflow-y-auto pr-3 scroll-thin space-y-3">
            <p className="text-xs text-muted">{roleTitleById[viewApplicant.role_id] || 'Unknown role'}</p>
            {viewApplicant.availability && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted">Availability</p>
                <p className="text-sm text-ink">{viewApplicant.availability}</p>
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {viewApplicant.cv_link && (
                <a
                  href={viewApplicant.cv_link}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-light-gold text-bronze"
                >
                  CV <IconExternalLink size={12} stroke={1.8} />
                </a>
              )}
              {viewApplicant.intro_video_link && (
                <a
                  href={viewApplicant.intro_video_link}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-light-gold text-bronze"
                >
                  Intro <IconExternalLink size={12} stroke={1.8} />
                </a>
              )}
              {viewApplicant.test_links &&
                viewApplicant.test_links.split(/\s+/).filter(Boolean).map((link, i) => (
                  <a
                    key={link}
                    href={link}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-light-gold text-bronze"
                  >
                    Test {viewApplicant.test_links.split(/\s+/).filter(Boolean).length > 1 ? i + 1 : ''}{' '}
                    <IconExternalLink size={12} stroke={1.8} />
                  </a>
                ))}
            </div>

            {viewApplicantCriteria.length > 0 && (
              <div className="border-t border-border pt-3 space-y-3">
                {viewApplicantCriteria.map((c) => {
                  const answer = viewApplicantAnswers[c.criteria_id];
                  if (!answer) return null;
                  return (
                    <div key={c.criteria_id}>
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted">{c.label}</p>
                      <p className="text-sm text-ink whitespace-pre-wrap">{answer}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </ConfirmModal>
    </>
  );
}
